"use client";

import { Button } from "@saridana-core/react-ui-kit";
import { ColorVariant, IImage, LinkTarget } from "@shared/types";
import { ButtonLink } from "@shared/ui";
import Image from "next/image";
import Link from "next/link";

export function HeroButton({
  variant,
  link,
  linkText,
  linkIcon,
  target,
  hideShine,
}: {
  variant: ColorVariant;
  link: string;
  linkText: string;
  linkIcon?: IImage;
  target?: LinkTarget;
  hideShine?: boolean;
}) {
  const backgroundMap: Record<ColorVariant, string | null> = {
    default: null,
    primary: "linear-gradient(180deg, hsl(var(--primary-200)) 16%, hsl(var(--primary-300)) 32.1%, hsl(var(--primary-700)) 114%)",
    secondary: "linear-gradient(180deg, hsl(var(--secondary-200)) 16%, hsl(var(--secondary-300)) 32.1%, hsl(var(--secondary-700)) 114%)",
    tertiary: "linear-gradient(180deg, hsl(var(--tertiary-200)) 16%, hsl(var(--tertiary-300)) 32.1%, hsl(var(--tertiary-700)) 114%)",
    quaternary:
      "linear-gradient(180deg, hsl(var(--quaternary-200)) 16%, hsl(var(--quaternary-300)) 32.1%, hsl(var(--quaternary-700)) 114%)",
  };

  const borderMap: Record<ColorVariant, string | null> = {
    default: null,
    primary: "hsl(var(--primary-200))",
    secondary: "hsl(var(--secondary-200))",
    tertiary: "hsl(var(--tertiary-200))",
    quaternary: "hsl(var(--quaternary-200))",
  };

  const textColorMap: Record<ColorVariant, string | null> = {
    default: null,
    primary: "hsl(var(--primary-800))",
    secondary: "hsl(var(--secondary-800))",
    tertiary: "hsl(var(--tertiary-800))",
    quaternary: "hsl(var(--quaternary-800))",
  };

  const textShadowMap: Record<ColorVariant, string | null> = {
    default: null,
    primary: "hsl(var(--primary-200))",
    secondary: "hsl(var(--secondary-200))",
    tertiary: "hsl(var(--tertiary-200))",
    quaternary: "hsl(var(--quaternary-200))",
  };

  if (variant === "default") {
    return (
      <div>
        <ButtonLink href={link} icon={linkIcon} text={linkText} target={target || "_self"} />
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        {!hideShine && (
          <Image
            src="/images/shine.webp"
            alt="Shine Button"
            className="absolute top-[-32px] left-[-15px] w-[100px] h-auto invert brightness-0"
            width="176"
            height="114"
          />
        )}
        <Button
          variant="outline"
          className="relative border-2 rounded-full font-bold text-lg md:text-xl py-4 px-6 md:px-10 h-14 md:h-[60px] hover:opacity-90"
          style={{
            background: backgroundMap[variant] as string,
            borderColor: borderMap[variant] as string,
            color: textColorMap[variant] as string,
            textShadow: "0px 1px 0px 0px hsla(47, 100%, 83%, 1)",
            boxShadow: "0px 3px 6px 0px hsla(28, 86%, 14%, 0.24), 0px 0px 12px 0px hsla(47, 100%, 83%, 1) inset",
          }}
          asChild
        >
          <Link href={link} target={target || "_self"}>
            {linkIcon && linkIcon.data && (
              <Image
                src={linkIcon.data?.attributes?.url || ""}
                className="size-7 mr-2"
                width={linkIcon.data?.attributes?.width || 28}
                height={linkIcon.data?.attributes?.height || 28}
                alt={linkIcon.data?.attributes?.alternativeText || linkIcon.data?.attributes?.name || ""}
              />
            )}
            <label className="relative inline-block font-bold cursor-pointer">
              <span
                className="absolute inset-0"
                style={{
                  color: textShadowMap[variant] as string,
                  transform: "translateY(1px) translateX(1px)",
                }}
              >
                {linkText}
              </span>

              <span
                className="relative"
                style={{
                  color: textColorMap[variant] as string,
                }}
              >
                {linkText}
              </span>
            </label>
          </Link>
        </Button>
      </div>
    </>
  );
}
